import { GraduationCap, TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { resourcesNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BADGES_HREF,
  CHEAPEST_FIX,
  OFFICE_COLUMNS,
  QUEUED_COHORTS,
  QUEUED_PANEL,
  SINGLE_POINTS,
  SINGLE_POINTS_PANEL,
  SKILLS_CHROME,
  SKILLS_COPY,
  SKILLS_KPIS,
  SKILLS_TABLE,
  SKILL_ROWS,
  type Coverage,
  type Segment,
  type SkillMatrixRow,
} from '@/demo/content/skillsMatrix'
import { cn } from '@/lib/utils'

/** Coverage band → the canonical AA pill tone. Never colour alone: the band's word renders. */
const COVERAGE_TONE: Record<Coverage, StatusTone> = {
  Healthy: 'done',
  Thin: 'review',
  Critical: 'blocked',
}

/**
 * Row weight is monotonic with severity: Critical carries the full rose wash
 * and the heaviest rule, Thin a lighter amber one, Healthy none. An exception
 * row is never quieter than a lesser row.
 */
const ROW_WASH: Record<Coverage, string> = {
  Critical: 'bg-blocked-bg',
  Thin: 'bg-review-bg/60',
  Healthy: '',
}

const ROW_RULE: Record<Coverage, string> = {
  Critical: 'border-l-[3px] border-l-rose-ink',
  Thin: 'border-l-2 border-l-review',
  Healthy: 'border-l-[3px] border-l-transparent',
}

/** Prose in which some words are real destinations (never a dead phrase). */
function Prose({ segments, className }: { segments: readonly Segment[]; className?: string }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          <span key={index}>{segment.text}</span>
        ) : (
          <Link
            key={index}
            to={segment.href}
            className={cn('font-medium underline underline-offset-2 hover:no-underline', className)}
          >
            {segment.label}
          </Link>
        )
      )}
    </>
  )
}

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

function SkillRow({ row }: { row: SkillMatrixRow }) {
  const { holders = [] } = row
  const critical = row.coverage === 'Critical'

  return (
    <TableRow className={ROW_WASH[row.coverage]}>
      <TableHead
        scope="row"
        className={cn('h-auto max-w-[18rem] whitespace-normal py-2.5 align-top', ROW_RULE[row.coverage])}
      >
        <span className={cn('block text-sm text-foreground', critical && 'font-semibold')}>
          {row.name}
        </span>
        {holders.length > 0 && (
          <span className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-xs font-normal text-muted-foreground">
            <span>{SKILLS_TABLE.holdersPrefix}</span>
            {holders.map((holder, index) => (
              <span key={holder.id} className="inline-flex items-baseline gap-1.5">
                {index > 0 && <span aria-hidden>·</span>}
                <Link
                  to={holder.href}
                  className="text-ink-soft underline underline-offset-2 hover:text-foreground"
                >
                  {holder.label}
                </Link>
              </span>
            ))}
          </span>
        )}
      </TableHead>

      {OFFICE_COLUMNS.map((office) => {
        const heads = row.heads[office.code]
        return (
          <TableCell
            key={office.code}
            className={cn(
              'v-figure py-2.5 text-right text-sm tabular-nums',
              heads === 0 ? 'text-muted-foreground' : 'text-foreground',
              critical && heads > 0 && 'font-semibold'
            )}
          >
            {heads}
          </TableCell>
        )
      })}

      <TableCell className="py-2.5 text-right">
        <Link
          to={BADGES_HREF}
          title={SKILLS_TABLE.certifiedLinkTitle}
          className="v-figure text-sm tabular-nums text-foreground underline underline-offset-2 hover:no-underline"
        >
          {SKILLS_TABLE.certifiedLabel(row.certified, row.totalHeads)}
        </Link>
      </TableCell>

      <TableCell className="v-figure py-2.5 text-right text-sm tabular-nums text-foreground">
        {row.demandLabel}
      </TableCell>

      <TableCell className="whitespace-normal py-2.5">
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <StatusPill tone={COVERAGE_TONE[row.coverage]}>{row.coverage}</StatusPill>
          <span className="text-xs text-muted-foreground">
            {SKILLS_TABLE.coverageMeaning[row.coverage]}
          </span>
        </span>
      </TableCell>
    </TableRow>
  )
}

/**
 * 1.5 Skills matrix — skills by office read against live and pipeline demand.
 * Needs-attention first: Critical rows carry the rose wash, Thin the amber
 * one, and the panels below are derived from those same rows, so the argument
 * the table makes and the fix the page proposes cannot disagree.
 */
export default function SkillsMatrixPage() {
  return (
    <AppShell
      moduleLabel={SKILLS_CHROME.moduleLabel}
      breadcrumb={SKILLS_CHROME.breadcrumb}
      contextPill={SKILLS_CHROME.contextPill}
      nav={resourcesNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{SKILLS_COPY.title}</h1>
      {/* PX-W6.2 (AC-8): the demo badge — this surface renders seed content and stays network-quiet. */}
      <ConceptBadge />
        <p className="text-sm text-muted-foreground">{SKILLS_COPY.lede}</p>
      </header>

      <section className="mt-5 rounded-lg border border-border bg-card" aria-label={SKILLS_COPY.title}>
        <Table>
          <TableCaption className="sr-only">{SKILLS_TABLE.caption}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col" className={cn(HEAD_CLASS, 'pl-3')}>
                {SKILLS_TABLE.columns.skill}
              </TableHead>
              {OFFICE_COLUMNS.map((office) => (
                <TableHead key={office.code} scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                  <span aria-hidden>{office.code}</span>
                  <span className="sr-only">{office.name}</span>
                </TableHead>
              ))}
              <TableHead scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                {SKILLS_TABLE.columns.certified}
              </TableHead>
              <TableHead scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                {SKILLS_TABLE.columns.demand}
              </TableHead>
              <TableHead scope="col" className={HEAD_CLASS}>
                {SKILLS_TABLE.columns.coverage}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SKILL_ROWS.map((row) => (
              <SkillRow key={row.key} row={row} />
            ))}
          </TableBody>
        </Table>
      </section>

      <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard
          label={SKILLS_KPIS.critical.label}
          figure={SKILLS_KPIS.critical.figure}
          qualifier={SKILLS_KPIS.critical.qualifier}
        />
        <KpiCard
          label={SKILLS_KPIS.thin.label}
          figure={SKILLS_KPIS.thin.figure}
          qualifier={SKILLS_KPIS.thin.qualifier}
        />
        <KpiCard
          label={SKILLS_KPIS.certified.label}
          figure={SKILLS_KPIS.certified.figure}
          qualifier={SKILLS_KPIS.certified.qualifier}
        />
        <KpiCard
          label={SKILLS_KPIS.demand.label}
          figure={SKILLS_KPIS.demand.figure}
          qualifier={SKILLS_KPIS.demand.qualifier}
        />
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {/* Where one person is the whole capability — derived from the Critical rows. */}
        <section
          aria-labelledby="single-points-label"
          className="rounded-lg border border-border border-l-[3px] border-l-rose-ink bg-card px-4 py-4"
        >
          <div id="single-points-label" className="flex items-center gap-1.5">
            <TriangleAlert className="h-3.5 w-3.5 text-rose-ink" aria-hidden />
            <SectionLabel>{SINGLE_POINTS_PANEL.label}</SectionLabel>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{SINGLE_POINTS_PANEL.lede}</p>
          <ul className="mt-3 space-y-3">
            {SINGLE_POINTS.map((point) => (
              <li key={point.key} className="border-t border-hairline pt-3 first:border-t-0 first:pt-0">
                <p className="text-sm leading-relaxed text-foreground">
                  <Prose segments={point.prose} className="text-rose-ink" />
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{point.meta}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* What the gap costs to close, against the alternative. */}
        <section
          aria-labelledby="cheapest-fix-label"
          className="rounded-lg border border-border bg-card px-4 py-4"
        >
          <div id="cheapest-fix-label">
            <SectionLabel>{CHEAPEST_FIX.label}</SectionLabel>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground">
            <Prose segments={CHEAPEST_FIX.prose} className="text-accent-foreground" />
          </p>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">{CHEAPEST_FIX.comparison}</p>
        </section>
      </div>

      {/* What PX proposes off this matrix — mint. */}
      <section aria-labelledby="queued-label" className="mt-4 rounded-lg bg-accent px-4 py-4">
        <div id="queued-label" className="flex items-center gap-1.5">
          <GraduationCap className="h-3.5 w-3.5 text-accent-foreground" aria-hidden />
          <SectionLabel>{QUEUED_PANEL.label}</SectionLabel>
        </div>
        <ul className="mt-3 flex flex-wrap gap-2">
          {QUEUED_COHORTS.map((cohort) => (
            <li key={cohort.key}>
              <Link
                to={cohort.href}
                className="flex items-baseline gap-2 rounded-md border border-border bg-card px-3 py-2 hover:bg-muted"
              >
                <span className="text-sm font-medium text-foreground">{cohort.label}</span>
                <Badge variant="outline" className="font-normal">
                  {QUEUED_PANEL.peopleLabel(cohort.people)}
                </Badge>
                <span className="v-figure text-xs tabular-nums text-muted-foreground">
                  {cohort.startWeekLabel}
                </span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground">
          <Prose segments={QUEUED_PANEL.trigger} className="text-accent-foreground" />
        </p>
      </section>

      <DensityNote>{SKILLS_COPY.densityNote}</DensityNote>
    </AppShell>
  )
}
